import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';

import { iaRa } from 'lib/helpers/utility';
import { useOrderResult } from 'lib/hooks/queries/orders/useOrderResult';

import applyChangesToPDF from 'components/shared/DocumentEditor/lib/applyChangesToPDF';

const useDocumentPreview = ({ activeView, setActiveView }) => {
  const [documentSrc, setDocumentSrc] = useState(null);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [canvasStates, setCanvasStates] = useState({});
  const [scale, setScale] = useState(1.5);

  const navigate = useNavigate();
  const { myPortalPractice } = useParams();
  const [searchParams] = useSearchParams();

  const resultIdFromParams = searchParams.get('result_id');
  const itemProps = activeView?.itemProps;
  const viewData = activeView?.data;

  const result = useMemo(() => {
    if (viewData?.result) return viewData?.result;

    if (resultIdFromParams) {
      const res = findResultById(itemProps?.labOrderResults, resultIdFromParams);
      if (!res) return null;
      return { ...res, activeVersion: viewData?.result?.activeVersion };
    }

    return viewData?.result;
  }, [itemProps?.labOrderResults, resultIdFromParams, viewData?.result]);

  const { data: documentHistory } = useQuery({
    queryKey: ['document-history', result?.id, 'labsoft_order_result', myPortalPractice],
    enabled: Boolean(result?.id)
  });

  const { data: resultDocumentData } = useOrderResult({
    dependencies: [result?.report_url],
    params: { key: result?.report_url }
  });

  useEffect(() => {
    loadLatestDocumentVersion();
  }, [resultDocumentData, documentHistory, result?.activeVersion]);

  const loadLatestDocumentVersion = () => {
    const modalHistory = viewData?.result?.history;
    const historyRecords = documentHistory?.records;
    const latestRecord = resolveCurrentVersion(modalHistory, historyRecords);

    if (!latestRecord && latestRecord?.version === currentVersion && documentSrc) return;

    setCurrentVersion(latestRecord?.version);
    loadDocumentBlob({ record: latestRecord });
  };

  const loadDocumentBlob = async (recordParam) => {
    if (!resultDocumentData?.data?.url) return;

    const record = recordParam && 'record' in recordParam ? recordParam : { record: recordParam };

    const {
      blob,
      scale,
      changes = {}
    } = await applyChangesToPDF({
      pdf: resultDocumentData?.data?.url,
      navigate,
      id: result?.id,
      historyData: record ?? null,
      kind: 'labsoft_order_result'
    });

    if (record?.record) {
      setActiveView((prev) => ({
        ...prev,
        data: {
          ...prev.data,
          result: {
            ...prev.data.result,
            activeVersion: record.record.version
          }
        }
      }));
    }

    setDocumentSrc(blob ? URL.createObjectURL(blob) : documentSrc);
    setScale(scale);
    setCanvasStates(changes);
  };

  const resolveCurrentVersion = (modalHistory, serverHistory) => {
    const historyData = iaRa(
      (serverHistory?.length ?? 0) > (modalHistory?.length ?? 0) ? serverHistory : modalHistory
    );

    return (
      (result?.activeVersion &&
        historyData.find((entry) => entry.version === result.activeVersion)) ||
      historyData.find((entry) => entry.selected) ||
      historyData.reduce(
        (latest, entry) => (!latest.version || entry.version > latest.version ? entry : latest),
        null
      )
    );
  };

  return {
    documentSrc: documentSrc || resultDocumentData?.url,
    resultDocumentData,
    result,
    canvasStates,
    scale,

    loadDocument: loadDocumentBlob,
    loadLatestVersion: loadLatestDocumentVersion,
    setCanvasStates,
    setScale
  };
};

const findResultById = (results, id) => results?.find((result) => result.id === Number(id));

export default useDocumentPreview;
